import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminJson, getAdminSession } from "../utils/adminSession";
import { activityLineWithoutWhen, formatAdminDateTime } from "../utils/formatAdminDate";

const KL_TZ = "Asia/Kuala_Lumpur";
const POPULAR_PAGE_SIZE = 4;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SOURCE_META = [
  { source: "organic", label: "Organic Search", color: "#c4a574" },
  { source: "direct", label: "Direct", color: "#141414" },
  { source: "social", label: "Social", color: "#9ca3af" },
  { source: "referral", label: "Referral", color: "#e8dcc8" },
];

function pad2(value) {
  return String(value).padStart(2, "0");
}

function klParts(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: KL_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour: Number(get("hour")),
  };
}

function klDay(value) {
  const { year, month, day } = klParts(value);
  if (!year || !month || !day) return "";
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function lastDayOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function shiftDay(day, delta) {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
}

function currentMonthRange() {
  const { year, month } = klParts();
  return {
    from: `${year}-${pad2(month)}-01`,
    to: `${year}-${pad2(month)}-${pad2(lastDayOfMonth(year, month))}`,
  };
}

function last30DaysRange() {
  const to = klDay(new Date());
  return { from: shiftDay(to, -29), to };
}

function inclusiveDays(from, to) {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  return Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
}

function greetingWord(now = new Date()) {
  const hour = klParts(now).hour;
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function formatCount(value) {
  return Number(value || 0).toLocaleString("en-US");
}

function formatDayLabel(value) {
  const [, month, day] = String(value || "").split("-");
  if (!month || !day) return value;
  return `${MONTHS[Number(month) - 1]} ${Number(day)}`;
}

function formatRelativeWhen(value) {
  return formatAdminDateTime(value);
}

function pageLabel(item) {
  const path = String(item?.path || "").trim();
  return path || "/";
}

function changeLabel(current, previous) {
  if (!previous && !current) return { text: "No change vs. previous period", tone: "flat" };
  if (!previous && current) return { text: "New vs. previous period", tone: "up" };
  const pct = ((current - previous) / previous) * 100;
  const rounded = Math.abs(pct) >= 10 ? pct.toFixed(1) : pct.toFixed(1);
  const sign = pct > 0 ? "+" : "";
  return {
    text: `${sign}${rounded}% vs. previous period`,
    tone: pct > 0 ? "up" : pct < 0 ? "down" : "flat",
  };
}

function inRange(value, from, to) {
  const day = klDay(value);
  return Boolean(day && day >= from && day <= to);
}

function activityLine(item) {
  return activityLineWithoutWhen(item?.text) || String(item?.text || "").trim();
}

function Sparkline({ values, color = "#c4a574" }) {
  const points = values.length ? values : [0];
  const max = Math.max(1, ...points);
  const width = 72;
  const height = 28;
  const path = points
    .map((value, index) => {
      const x = points.length === 1 ? width / 2 : (index / (points.length - 1)) * width;
      const y = height - (value / max) * (height - 2) - 1;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-7 w-[72px]" aria-hidden="true">
      <polyline fill="none" stroke={color} strokeWidth="1.6" points={path} />
    </svg>
  );
}

function TrafficChart({ days }) {
  const width = 720;
  const height = 220;
  const pad = { top: 16, right: 16, bottom: 28, left: 40 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const maxValue = Math.max(1, ...days.map((day) => Math.max(day.pageViews, day.visitors)));
  const pointsFor = (key) =>
    days.map((day, index) => {
      const x = pad.left + (days.length === 1 ? innerW / 2 : (index / (days.length - 1)) * innerW);
      const y = pad.top + innerH - (day[key] / maxValue) * innerH;
      return `${x},${y}`;
    });
  const labels = days.filter((_, index) => {
    if (days.length <= 8) return true;
    const step = Math.ceil(days.length / 7);
    return index === 0 || index === days.length - 1 || index % step === 0;
  });

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-44 w-full max-w-full sm:h-56" role="img" aria-label="Website traffic by day">
      {[0.25, 0.5, 0.75, 1].map((mark) => {
        const y = pad.top + innerH - mark * innerH;
        return <line key={mark} x1={pad.left} y1={y} x2={width - pad.right} y2={y} stroke="#f3f3f3" />;
      })}
      <line x1={pad.left} y1={height - pad.bottom} x2={width - pad.right} y2={height - pad.bottom} stroke="#e5e5e5" />
      <polyline fill="none" stroke="#c4a574" strokeWidth="2.2" points={pointsFor("pageViews").join(" ")} />
      <polyline fill="none" stroke="#d8c4a0" strokeWidth="2" points={pointsFor("visitors").join(" ")} />
      {labels.map((day) => {
        const index = days.indexOf(day);
        const x = pad.left + (days.length === 1 ? innerW / 2 : (index / (days.length - 1)) * innerW);
        return (
          <text key={day.date} x={x} y={height - 8} textAnchor="middle" className="fill-neutral-400" fontSize="10">
            {formatDayLabel(day.date)}
          </text>
        );
      })}
    </svg>
  );
}

function SourceDonut({ sources, visitors }) {
  const radius = 46;
  const circ = 2 * Math.PI * radius;
  let offset = 0;
  const total = sources.reduce((sum, item) => sum + (item.pageViews || 0), 0);

  return (
    <div className="flex min-w-0 flex-col items-center gap-5 sm:flex-row sm:items-center sm:gap-6">
      <div className="relative h-36 w-36 shrink-0 sm:h-40 sm:w-40">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#f3f3f3" strokeWidth="14" />
          {sources.map((item) => {
            const meta = SOURCE_META.find((row) => row.source === item.source);
            const length = total ? (item.pageViews / total) * circ : 0;
            const circle = (
              <circle
                key={item.source}
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke={meta?.color || "#d4d4d4"}
                strokeWidth="14"
                strokeDasharray={`${length} ${circ - length}`}
                strokeDashoffset={-offset}
              />
            );
            offset += length;
            return circle;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <p className="text-xl font-semibold text-at-dark">{formatCount(visitors)}</p>
          <p className="text-[10px] uppercase tracking-[0.14em] text-neutral-400">Visitors</p>
        </div>
      </div>
      <ul className="w-full min-w-0 flex-1 space-y-2.5">
        {SOURCE_META.map((meta) => {
          const row = sources.find((item) => item.source === meta.source) || { pageViews: 0, percent: 0 };
          return (
            <li key={meta.source} className="flex items-center justify-between gap-3 text-sm">
              <span className="flex items-center gap-2 text-neutral-600">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: meta.color }} />
                {meta.label}
              </span>
              <span className="font-medium text-at-dark">{row.percent || 0}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PeopleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="1.7">
      <circle cx="9" cy="8" r="3" />
      <path d="M4 18a5 5 0 0110 0" />
      <circle cx="16.5" cy="9" r="2.2" />
    </svg>
  );
}

function ViewsIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M4 18V8M10 18V4M16 18v-6M22 18H3" />
    </svg>
  );
}

function QuoteIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M6 5h12v14H6z" />
      <path d="M9 9h6M9 13h4" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M4 7h16v10H4z" />
      <path d="M4 7l8 6 8-6" />
    </svg>
  );
}

function BoxIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M4 8l8-4 8 4-8 4z" />
      <path d="M4 8v8l8 4 8-4V8" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M4 8h6l2 2h8v8H4z" />
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M3 8h11v8H3z" />
      <path d="M14 11h4l3 3v2h-7z" />
    </svg>
  );
}

function AdminPage() {
  const navigate = useNavigate();
  const defaults = useMemo(() => last30DaysRange(), []);
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [applied, setApplied] = useState(defaults);
  const [data, setData] = useState(null);
  const [forms, setForms] = useState([]);
  const [counts, setCounts] = useState({ products: 0, categories: 0, deliveries: 0 });
  const [activities, setActivities] = useState([]);
  const [adminName, setAdminName] = useState(String(getAdminSession()?.name || "").trim() || "Admin");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [popularPage, setPopularPage] = useState(1);

  useEffect(() => {
    const session = getAdminSession();
    if (!session?.email) return undefined;
    let cancelled = false;
    adminJson("/admins")
      .then((list) => {
        if (cancelled) return;
        const current = Array.isArray(list)
          ? list.find((item) => String(item.email || "").toLowerCase() === String(session.email).toLowerCase())
          : null;
        setAdminName(String(current?.name || session.name || "").trim() || "Admin");
      })
      .catch(() => {
        if (!cancelled) setAdminName(String(session.name || "").trim() || "Admin");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([
      adminJson(`/analytics?from=${applied.from}&to=${applied.to}`),
      adminJson("/forms"),
      adminJson("/products"),
      adminJson("/categories"),
      adminJson("/deliveries"),
      adminJson("/logs?page=1"),
    ])
      .then(([analytics, formList, productList, categoryList, deliveryList, logData]) => {
        if (cancelled) return;
        setData(analytics);
        setForms(Array.isArray(formList) ? formList : []);
        setCounts({
          products: Array.isArray(productList) ? productList.length : 0,
          categories: Array.isArray(categoryList) ? categoryList.length : 0,
          deliveries: Array.isArray(deliveryList) ? deliveryList.length : 0,
        });
        setActivities(Array.isArray(logData?.items) ? logData.items.slice(0, 4) : []);
        setPopularPage(1);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        setError(err.message || "Unable to load dashboard");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applied.from, applied.to, navigate]);

  function applyRange(event) {
    event.preventDefault();
    if (!from || !to) {
      setError("Choose both From and To dates");
      return;
    }
    if (from > to) {
      setError("From date must be on or before To date");
      return;
    }
    setApplied({ from, to });
  }

  function applyPreset(next) {
    setFrom(next.from);
    setTo(next.to);
    setApplied(next);
  }

  const visitors = data?.visitors || 0;
  const pageViews = data?.pageViews || 0;
  const days = Array.isArray(data?.days) ? data.days : [];
  const sources = Array.isArray(data?.sources) ? data.sources : [];
  const popularPages = Array.isArray(data?.popularPages) ? data.popularPages : [];
  const popularPageCount = Math.max(1, Math.ceil(popularPages.length / POPULAR_PAGE_SIZE));
  const popularPageSafe = Math.min(popularPage, popularPageCount);
  const visiblePopularPages = popularPages.slice(
    (popularPageSafe - 1) * POPULAR_PAGE_SIZE,
    popularPageSafe * POPULAR_PAGE_SIZE
  );
  const previous = data?.previous || { visitors: 0, pageViews: 0 };
  const periodDays = inclusiveDays(applied.from, applied.to);
  const periodLabel = `${periodDays} day${periodDays === 1 ? "" : "s"}`;

  const quotes = forms.filter((item) => inRange(item.createdAt, applied.from, applied.to));
  const prevQuotes = forms.filter((item) => {
    const prevFrom = data?.previous?.from;
    const prevTo = data?.previous?.to;
    return Boolean(prevFrom && prevTo && inRange(item.createdAt, prevFrom, prevTo));
  });
  const unreadForms = forms.filter((item) => item.read !== true);
  const recentForms = forms.slice(0, 4);
  const quoteDays = days.map((day) => quotes.filter((item) => klDay(item.createdAt) === day.date).length);
  const formDays = days.map((day) => forms.filter((item) => inRange(item.createdAt, applied.from, applied.to) && klDay(item.createdAt) === day.date).length);
  const contentMax = Math.max(1, counts.products, counts.categories, counts.deliveries);
  const visitorChange = changeLabel(visitors, previous.visitors || 0);
  const viewChange = changeLabel(pageViews, previous.pageViews || 0);
  const quoteChange = changeLabel(quotes.length, prevQuotes.length);
  const moreVisitors = changeLabel(visitors, previous.visitors || 0);

  const cards = [
    {
      label: "Total Visitors",
      value: formatCount(visitors),
      change: visitorChange,
      values: days.map((day) => day.visitors),
      icon: PeopleIcon,
    },
    {
      label: "Page Views",
      value: formatCount(pageViews),
      change: viewChange,
      values: days.map((day) => day.pageViews),
      icon: ViewsIcon,
    },
    {
      label: "Enquiries",
      value: formatCount(quotes.length),
      change: quoteChange,
      values: quoteDays,
      icon: QuoteIcon,
    },
    {
      label: "Unread Forms",
      value: formatCount(unreadForms.length),
      change: unreadForms.length ? { text: "Needs attention", tone: "warn" } : { text: "All caught up", tone: "flat" },
      values: formDays,
      icon: MailIcon,
    },
  ];

  const contentRows = [
    { label: "Products", count: counts.products, to: "/admin-products", icon: BoxIcon },
    { label: "Categories", count: counts.categories, to: "/admin-category", icon: FolderIcon },
    { label: "Deliveries", count: counts.deliveries, to: "/admin-deliveries", icon: TruckIcon },
  ];

  return (
    <div className="min-w-0 max-w-full space-y-5 overflow-x-hidden">
      <div className="flex min-w-0 flex-col gap-4">
        <div className="min-w-0">
          <h1 className="break-words font-serif text-2xl text-at-dark sm:text-3xl">
            {greetingWord()}, {adminName}
          </h1>
          <p className="mt-1 text-sm text-neutral-500">Here&apos;s what&apos;s happening on your website.</p>
        </div>
        <form
          className="grid min-w-0 w-full grid-cols-2 gap-2 md:flex md:flex-nowrap md:items-end"
          onSubmit={applyRange}
        >
          <label className="min-w-0 text-xs text-neutral-500 md:w-40">
            From
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="mt-1 block w-full min-w-0 rounded-md border border-neutral-200 bg-white px-2 py-2 text-sm text-at-dark outline-none focus:border-at-gold sm:px-3"
            />
          </label>
          <label className="min-w-0 text-xs text-neutral-500 md:w-40">
            To
            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="mt-1 block w-full min-w-0 rounded-md border border-neutral-200 bg-white px-2 py-2 text-sm text-at-dark outline-none focus:border-at-gold sm:px-3"
            />
          </label>
          <button
            type="submit"
            className="rounded-md bg-at-dark px-3 py-2 text-sm text-white hover:bg-at-darker md:shrink-0 md:whitespace-nowrap"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => applyPreset(last30DaysRange())}
            className="rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-at-dark hover:border-neutral-300 md:shrink-0 md:whitespace-nowrap"
          >
            Last 30 days
          </button>
          <button
            type="button"
            onClick={() => applyPreset(currentMonthRange())}
            className="col-span-2 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-at-dark hover:border-neutral-300 sm:col-span-1 md:shrink-0 md:whitespace-nowrap"
          >
            This month
          </button>
        </form>
      </div>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <section className="grid min-w-0 grid-cols-2 gap-3 xl:grid-cols-4 xl:gap-4">
        {cards.map((card) => (
          <article key={card.label} className="min-w-0 rounded-xl border border-neutral-200 bg-white px-3 py-3 sm:px-5 sm:py-4">
            <div className="flex items-start justify-between gap-2 sm:gap-3">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-xs text-neutral-500 sm:gap-2 sm:text-sm">
                  <card.icon />
                  {card.label}
                </p>
                <p className="mt-2 text-2xl font-semibold text-at-dark sm:mt-3 sm:text-3xl">{loading ? "—" : card.value}</p>
                <p
                  className={`mt-2 break-words text-xs ${
                    card.change.tone === "up"
                      ? "text-emerald-600"
                      : card.change.tone === "down"
                        ? "text-red-600"
                        : card.change.tone === "warn"
                          ? "text-red-600"
                          : "text-neutral-400"
                  }`}
                >
                  {card.change.tone === "up" ? "▲ " : card.change.tone === "down" ? "▼ " : card.change.tone === "warn" ? "● " : ""}
                  {card.change.text}
                </p>
              </div>
              <span className="hidden sm:block">
                <Sparkline values={card.values} />
              </span>
            </div>
          </article>
        ))}
      </section>

      <section className="grid min-w-0 gap-4 xl:grid-cols-3">
        <article className="min-w-0 overflow-hidden rounded-xl border border-neutral-200 bg-white xl:col-span-2">
          <div className="flex flex-col gap-2 border-b border-neutral-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <h2 className="text-sm font-semibold text-at-dark">Website Traffic</h2>
            <p className="text-xs text-neutral-400">
              <span className="mr-3 inline-block h-2 w-2 rounded-full bg-[#d8c4a0]" /> Visitors
              <span className="ml-4 mr-3 inline-block h-2 w-2 rounded-full bg-at-gold" /> Page Views
            </p>
          </div>
          {loading ? (
            <p className="px-5 py-10 text-sm text-neutral-500">Loading traffic…</p>
          ) : (
            <div className="min-w-0 overflow-hidden px-2 py-3 sm:px-3">
              <TrafficChart days={days} />
              <p className={`break-words px-2 pb-2 text-xs ${moreVisitors.tone === "up" ? "text-emerald-600" : moreVisitors.tone === "down" ? "text-red-600" : "text-neutral-400"}`}>
                {moreVisitors.tone === "up" ? "▲ " : moreVisitors.tone === "down" ? "▼ " : ""}
                {moreVisitors.text.startsWith("New")
                  ? `New vs. the previous ${periodLabel}`
                  : moreVisitors.text.replace("vs. previous period", `vs. the previous ${periodLabel}`)}
              </p>
            </div>
          )}
        </article>

        <article className="min-w-0 overflow-hidden rounded-xl border border-neutral-200 bg-white px-4 py-4 sm:px-5">
          <h2 className="text-sm font-semibold text-at-dark">Traffic Sources</h2>
          <div className="mt-6">
            {loading ? (
              <p className="text-sm text-neutral-500">Loading sources…</p>
            ) : (
              <SourceDonut sources={sources} visitors={visitors} />
            )}
          </div>
        </article>
      </section>

      <section className="grid min-w-0 gap-4 xl:grid-cols-3">
        <article className="min-w-0 overflow-hidden rounded-xl border border-neutral-200 bg-white xl:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-100 px-4 py-4 sm:px-5">
            <h2 className="text-sm font-semibold text-at-dark">Recent Form Submissions</h2>
            <Link to="/admin-form" className="shrink-0 text-sm text-neutral-500 hover:text-at-dark">
              View All Forms →
            </Link>
          </div>
          <div className="divide-y divide-neutral-100 md:hidden">
            {loading ? (
              <p className="px-4 py-8 text-sm text-neutral-500">Loading forms…</p>
            ) : recentForms.length ? (
              recentForms.map((item) => (
                <div key={item._id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 break-words text-sm text-at-dark">{item.name || "—"}</p>
                    {item.read ? (
                      <span className="shrink-0 rounded-full bg-neutral-100 px-2.5 py-1 text-xs text-neutral-500">Read</span>
                    ) : (
                      <span className="shrink-0 rounded-full bg-amber-50 px-2.5 py-1 text-xs text-amber-800">New</span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-neutral-400">{formatRelativeWhen(item.createdAt)}</p>
                </div>
              ))
            ) : (
              <p className="px-4 py-8 text-sm text-neutral-500">No form submissions yet.</p>
            )}
          </div>
          <div className="hidden min-w-0 overflow-x-auto md:block">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-[0.12em] text-neutral-400">
                <tr>
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Company</th>
                  <th className="px-5 py-3 font-medium">Subject</th>
                  <th className="px-5 py-3 font-medium">Received</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-neutral-500">
                      Loading forms…
                    </td>
                  </tr>
                ) : recentForms.length ? (
                  recentForms.map((item) => (
                    <tr key={item._id} className="border-t border-neutral-100">
                      <td className="px-5 py-3 text-at-dark">{item.name || "—"}</td>
                      <td className="px-5 py-3 text-neutral-600">{item.companyName || "—"}</td>
                      <td className="px-5 py-3 text-neutral-600">{item.subject || item.enquiry || "—"}</td>
                      <td className="px-5 py-3 text-neutral-500">{formatRelativeWhen(item.createdAt)}</td>
                      <td className="px-5 py-3">
                        {item.read ? (
                          <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs text-neutral-500">Read</span>
                        ) : (
                          <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs text-amber-800">New</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-neutral-500">
                      No form submissions yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="min-w-0 overflow-hidden rounded-xl border border-neutral-200 bg-white px-4 py-4 sm:px-5">
          <h2 className="text-sm font-semibold text-at-dark">Content Overview</h2>
          <ul className="mt-5 space-y-5">
            {contentRows.map((row) => (
              <li key={row.label}>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="flex items-center gap-2 text-neutral-600">
                    <row.icon />
                    {row.label}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="font-semibold text-at-dark">{formatCount(row.count)}</span>
                    <Link to={row.to} className="text-neutral-400 hover:text-at-dark">
                      Manage →
                    </Link>
                  </span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-100">
                  <div className="h-full bg-at-gold" style={{ width: `${Math.round((row.count / contentMax) * 100)}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </article>
      </section>

      <section className="grid min-w-0 gap-4 xl:grid-cols-2">
        <article className="min-w-0 overflow-hidden rounded-xl border border-neutral-200 bg-white px-4 py-4 sm:px-5">
          <h2 className="text-sm font-semibold text-at-dark">Popular Pages</h2>
          {loading ? (
            <p className="mt-6 text-sm text-neutral-500">Loading pages…</p>
          ) : popularPages.length ? (
            <>
              <ul className="mt-4 divide-y divide-neutral-100">
                {visiblePopularPages.map((item) => {
                  const percent = pageViews ? Math.round((item.pageViews / pageViews) * 100) : 0;
                  return (
                    <li key={item.path} className="py-2.5">
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <p className="min-w-0 break-all text-at-dark">{pageLabel(item)}</p>
                        <p className="shrink-0 text-neutral-600">{formatCount(item.pageViews)}</p>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-neutral-100">
                          <div className="h-full bg-at-gold" style={{ width: `${percent}%` }} />
                        </div>
                        <span className="w-8 shrink-0 text-right text-xs text-neutral-500">{percent}%</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {popularPages.length > POPULAR_PAGE_SIZE ? (
                <div className="mt-3 flex items-center justify-between text-xs text-neutral-500">
                  <p>
                    {popularPageSafe} / {popularPageCount}
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={popularPageSafe <= 1}
                      onClick={() => setPopularPage(popularPageSafe - 1)}
                      className="rounded-md border border-neutral-200 bg-white px-2 py-1 disabled:opacity-40"
                      aria-label="Previous popular pages"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      disabled={popularPageSafe >= popularPageCount}
                      onClick={() => setPopularPage(popularPageSafe + 1)}
                      className="rounded-md border border-neutral-200 bg-white px-2 py-1 disabled:opacity-40"
                      aria-label="Next popular pages"
                    >
                      ›
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <p className="mt-6 text-sm text-neutral-500">No public pages viewed in this range.</p>
          )}
        </article>

        <article className="min-w-0 overflow-hidden rounded-xl border border-neutral-200 bg-white px-4 py-4 sm:px-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-at-dark">Recent Activity</h2>
            <Link to="/admin-activities" className="shrink-0 text-sm text-neutral-500 hover:text-at-dark">
              View All Activity →
            </Link>
          </div>
          {loading ? (
            <p className="mt-6 text-sm text-neutral-500">Loading activity…</p>
          ) : activities.length ? (
            <ul className="mt-4 divide-y divide-neutral-100">
              {activities.map((item) => (
                <li key={item._id} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <p className="min-w-0 break-words text-sm text-at-dark">{activityLine(item)}</p>
                  <p className="shrink-0 text-xs text-neutral-400">{formatRelativeWhen(item.timestamp)}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-6 text-sm text-neutral-500">No recent activities.</p>
          )}
        </article>
      </section>
    </div>
  );
}

export default AdminPage;
